import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { FiChevronLeft, FiChevronRight, FiActivity, FiAlertCircle, FiSearch, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    action: '',
    start_date: '',
    end_date: '',
  });

  const token = localStorage.getItem('token');
  const fetchDataRef = useRef(null);

  // Get user data from localStorage or API
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  }, []);

  // Check if user is admin
  const isAdmin = user?.user_type === 'admin' || user?.roles?.some(r => r.name === 'Admin');

  // Fetch logs
  const fetchLogs = async (pageNum = 1, limitNum = 10, filtersObj = {}) => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError('');
    try {
      let url = `${process.env.REACT_APP_BASE_URL}user/audit/logs?page=${pageNum}&limit=${limitNum}`;
      
      // Add filter parameters to URL
      if (filtersObj.action) {
        url += `&action=${encodeURIComponent(filtersObj.action)}`;
      }
      if (filtersObj.start_date) {
        url += `&start_date=${filtersObj.start_date}`;
      }
      if (filtersObj.end_date) {
        url += `&end_date=${filtersObj.end_date}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data.data || []);
      setPage(response.data.page || pageNum);
      setLimit(response.data.limit || limitNum);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to fetch logs. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (isAdmin) {
      fetchLogs(1, limit, filters);
    }
  }, [isAdmin]);

  // Fetch when page, limit, or filters change
  useEffect(() => {
    if (!isAdmin) return;
    if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    fetchDataRef.current = setTimeout(() => {
      fetchLogs(page, limit, filters);
    }, 300);
    return () => {
      if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    };
  }, [page, limit, filters]);

  // Group logs by date
  const groupLogsByDate = () => {
    const grouped = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    logs.forEach((log) => {
      const logDate = new Date(log.created_at.split(' ')[0].split('-').reverse().join('-'));
      let dateLabel;

      if (logDate.toDateString() === today.toDateString()) {
        dateLabel = 'Today';
      } else if (logDate.toDateString() === yesterday.toDateString()) {
        dateLabel = 'Yesterday';
      } else {
        dateLabel = logDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      if (!grouped[dateLabel]) {
        grouped[dateLabel] = [];
      }
      grouped[dateLabel].push(log);
    });

    return grouped;
  };

  const groupedLogs = groupLogsByDate();
  const totalPages = Math.ceil(total / limit);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to page 1 when filters change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      action: '',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-5 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="text-6xl text-red-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can view the audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <FiActivity className="text-4xl text-blue-700" />
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800 tracking-tight">
            📋 Audit Logs
          </h1>
        </div>
        <p className="text-gray-600 text-base md:text-lg">
          Track all system activities and user actions
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 p-4 bg-white rounded-lg shadow-md border border-gray-200"
      >
        <div className="flex items-center gap-2 mb-3">
          <FiSearch className="text-blue-600 text-sm" />
          <h3 className="text-sm font-bold text-gray-800">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Action Input */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Action
            </label>
            <input
              type="text"
              placeholder="e.g., UNBLOCK_STOCK"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-xs"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-xs"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-xs"
            />
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              disabled={!filters.action && !filters.start_date && !filters.end_date}
              className="w-full px-3 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-medium text-xs flex items-center justify-center gap-2 transition"
            >
              <FiX size={14} />
              Clear
            </button>
          </div>
        </div>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-center gap-3 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg shadow-md"
        >
          <FiAlertCircle className="text-xl flex-shrink-0" />
          <span className="font-medium">{error}</span>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin">
              <FiActivity className="text-4xl text-blue-600" />
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading logs...</p>
          </div>
        </div>
      )}

      {/* Logs Container */}
      {!loading && logs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {Object.entries(groupedLogs).map(([dateLabel, dateLogs]) => (
            <div key={dateLabel} className="space-y-3">
              {/* Date Header */}
              <div className="flex items-center gap-3 sticky top-4 z-10">
                <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-transparent"></div>
                <span className="px-4 py-2 bg-white rounded-full text-sm font-bold text-blue-700 shadow-md border border-blue-200">
                  {dateLabel}
                </span>
                <div className="flex-1 h-px bg-gradient-to-l from-blue-300 to-transparent"></div>
              </div>

              {/* Logs for this date */}
              <div className="space-y-3 pl-0 md:pl-4">
                {dateLogs.map((log, idx) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white rounded-lg p-4 md:p-5 shadow-md hover:shadow-lg border border-gray-200 hover:border-blue-300 transition-all duration-200"
                  >
                    <div className="flex gap-4">
                      {/* Timeline dot */}
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 shadow-md"></div>
                        <div className="w-0.5 h-12 bg-gradient-to-b from-blue-300 to-transparent mt-2 last:hidden"></div>
                      </div>

                      {/* Log content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                          <p className="text-gray-800 font-semibold text-sm md:text-base break-words">
                            {log.log}
                          </p>
                          <span className="text-xs md:text-sm text-gray-500 font-medium whitespace-nowrap">
                            {log.created_at}
                          </span>
                        </div>

                        {/* Meta data */}
                        {log.meta_data && (
                          <details className="mt-3 cursor-pointer">
                            <summary className="text-xs text-blue-600 hover:text-blue-800 font-medium select-none">
                              📋 View Details
                            </summary>
                            <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-xs text-gray-700 space-y-2 max-h-48 overflow-y-auto">
                              {log.meta_data.query && Object.keys(log.meta_data.query).length > 0 && (
                                <div>
                                  <p className="font-semibold text-gray-800">Query:</p>
                                  <p className="ml-2 text-gray-600 font-mono break-all">
                                    {JSON.stringify(log.meta_data.query, null, 2)}
                                  </p>
                                </div>
                              )}
                              {log.meta_data.userAgent && (
                                <div>
                                  <p className="font-semibold text-gray-800">User Agent:</p>
                                  <p className="ml-2 text-gray-600 font-mono break-all text-xs">
                                    {log.meta_data.userAgent}
                                  </p>
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && logs.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <FiActivity className="text-6xl text-gray-300 mb-4" />
          <h3 className="text-2xl font-bold text-gray-600 mb-2">No Logs Found</h3>
          <p className="text-gray-500">There are no audit logs available at this time.</p>
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-10 flex flex-col md:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg shadow-md border border-gray-200"
        >
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1 || loading}
              onClick={() => setPage(Math.max(1, page - 1))}
              className="p-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg transition font-medium flex items-center gap-1"
            >
              <FiChevronLeft size={18} /> Previous
            </button>
            <span className="text-gray-700 font-semibold text-sm md:text-base">
              Page <span className="text-blue-600">{page}</span> of <span className="text-blue-600">{totalPages}</span>
            </span>
            <button
              disabled={page === totalPages || loading}
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              className="p-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg transition font-medium flex items-center gap-1"
            >
              Next <FiChevronRight size={18} />
            </button>
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-3">
            <span className="text-gray-700 font-medium text-sm">Logs per page:</span>
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white font-medium cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* Total count */}
          <span className="text-sm text-gray-600 font-medium">
            Total: <span className="text-blue-600 font-bold">{total}</span> logs
          </span>
        </motion.div>
      )}
    </div>
  );
}
